/* ============================================
   EXTENSION EXTRÊME — JAVASCRIPT
   ee-main.js
   Vanilla JS — Zero dependencies
   ============================================ */

(function() {
  'use strict';

  /* ---- Scroll Animations ---- */
  const initScrollAnimations = () => {
    const els = document.querySelectorAll('.ee-animate');
    if (!els.length) return;

    // FILET DE SÉCURITÉ : .ee-animate démarre à opacity:0. Sans IntersectionObserver,
    // le contenu resterait invisible pour toujours → on l'affiche immédiatement.
    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('ee-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('ee-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    els.forEach(el => observer.observe(el));

    // Affiche tout de suite ce qui est déjà dans l'écran, sans attendre le premier
    // callback de l'observateur : contenu visible instantanément au chargement.
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) {
        el.classList.add('ee-visible');
        observer.unobserve(el);
      }
    });
  };

  /* ---- Hero video : reboucle un peu avant la fin ----
     Le rebouclage natif (attribut loop) provoque un petit accroc visible chez
     certains navigateurs au moment du redémarrage. En sautant nous-mêmes à 0
     juste avant la vraie fin, on devance ce redémarrage natif — il n'a jamais
     l'occasion de se déclencher. L'attribut loop reste en filet de sécurité
     si ce script ne tourne pas. */
  const initHeroVideoLoop = () => {
    const video = document.getElementById('ee-hero-video');
    if (!video) return;
    video.addEventListener('timeupdate', () => {
      if (video.duration && video.duration - video.currentTime < 0.15) {
        video.currentTime = 0;
      }
    });
  };

  /* ---- Header Scroll ---- */
  const initHeaderScroll = () => {
    const header = document.getElementById('ee-header');
    if (!header) return;
    let lastScroll = 0;
    window.addEventListener('scroll', () => {
      const currentScroll = window.pageYOffset;
      if (currentScroll > 50) {
        header.classList.add('ee-scrolled');
      } else {
        header.classList.remove('ee-scrolled');
      }
      lastScroll = currentScroll;
    }, { passive: true });
  };

  /* ---- Mobile Menu ---- */
  const initMobileMenu = () => {
    const toggle = document.getElementById('ee-menu-toggle');
    const menu = document.getElementById('ee-mobile-menu');
    if (!toggle || !menu) return;

    toggle.addEventListener('click', () => {
      const isOpen = toggle.classList.contains('ee-active');
      toggle.classList.toggle('ee-active');
      menu.classList.toggle('ee-active');
      toggle.setAttribute('aria-expanded', !isOpen);
      menu.setAttribute('aria-hidden', isOpen);
      document.body.style.overflow = isOpen ? '' : 'hidden';
    });
  };

  /* ---- Accordion ---- */
  const initAccordions = () => {
    document.querySelectorAll('.ee-accordion__trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('.ee-accordion__item');
        const isActive = item.classList.contains('ee-active');
        
        // Close siblings
        item.parentElement.querySelectorAll('.ee-accordion__item.ee-active').forEach(active => {
          active.classList.remove('ee-active');
          active.querySelector('.ee-accordion__trigger').setAttribute('aria-expanded', 'false');
        });

        // Toggle current
        if (!isActive) {
          item.classList.add('ee-active');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  };

  /* ---- Cart Drawer ---- */
  const initCartDrawer = () => {
    const drawer = document.getElementById('ee-cart-drawer');
    if (!drawer) return;

    // Open
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        drawer.classList.add('ee-active');
        document.body.style.overflow = 'hidden';
        updateCartDrawer();
      });
    });

    // Close
    document.querySelectorAll('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', () => {
        drawer.classList.remove('ee-active');
        document.body.style.overflow = '';
      });
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('ee-active')) {
        drawer.classList.remove('ee-active');
        document.body.style.overflow = '';
      }
    });
  };

  /* ---- Update Cart Drawer ---- */
  const updateCartDrawer = () => {
    fetch(window.EE.routes.cart_url + '.js')
      .then(r => r.json())
      .then(cart => {
        const container = document.getElementById('ee-cart-items');
        const subtotal = document.getElementById('ee-cart-subtotal');
        const countEl = document.querySelector('[data-cart-count]');

        if (countEl) {
          if (cart.item_count > 0) {
            countEl.textContent = cart.item_count;
            countEl.style.display = 'flex';
          } else {
            countEl.style.display = 'none';
          }
        }

        if (subtotal) {
          subtotal.textContent = formatMoney(cart.total_price);
        }

        if (!container) return;

        if (cart.item_count === 0) {
          container.innerHTML = '<p style="text-align:center;color:var(--ee-grey);padding:3rem 0;">' + window.EE.strings.cartEmpty + '</p>';
          return;
        }

        let html = '';
        cart.items.forEach(item => {
          html += '<div class="ee-cart-item">';
          html += '<img class="ee-cart-item__image" src="' + escapeHtml(getSizedImageUrl(item.image, '160x')) + '" alt="' + escapeHtml(item.product_title) + '" width="80" height="80">';
          html += '<div class="ee-cart-item__details">';
          html += '<div class="ee-cart-item__title">' + escapeHtml(item.product_title) + '</div>';
          html += '<div class="ee-cart-item__variant">' + escapeHtml(item.variant_title || '') + '</div>';
          html += '<div class="ee-cart-item__bottom">';
          html += '<span class="ee-cart-item__price">' + formatMoney(item.final_line_price) + '</span>';
          html += '<button class="ee-cart-item__remove" data-remove-item="' + escapeHtml(item.key) + '">' + escapeHtml(window.EE.strings.cartRemove) + '</button>';
          html += '</div></div></div>';
        });
        container.innerHTML = html;

        // Bind remove buttons
        container.querySelectorAll('[data-remove-item]').forEach(btn => {
          btn.addEventListener('click', () => {
            const key = btn.getAttribute('data-remove-item');
            changeCartItem(key, 0);
          });
        });
      })
      .catch(err => console.error('Cart error:', err));
  };

  /* ---- Add to Cart ---- */
  const initAddToCart = () => {
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.matches('form[action*="/cart/add"]')) return;
      e.preventDefault();

      const btn = form.querySelector('[type="submit"]');
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.innerHTML = '<span class="ee-spinner"></span>';

      fetch(window.EE.routes.cart_add_url + '.js', {
        method: 'POST',
        body: new FormData(form)
      })
      .then(r => r.json())
      .then(item => {
        btn.textContent = window.EE.strings.addedShort;
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);
        updateCartDrawer();
        document.getElementById('ee-cart-drawer').classList.add('ee-active');
        document.body.style.overflow = 'hidden';
        showNotification(item.title + ' ' + window.EE.strings.addedToCartSuffix, 'success');
      })
      .catch(err => {
        btn.textContent = originalText;
        btn.disabled = false;
        showNotification(window.EE.strings.genericError, 'error');
      });
    });
  };

  /* ---- Ajout rapide depuis une carte produit ----
     Les produits à variante unique ont déjà un vrai formulaire dans la carte
     (intercepté par initAddToCart). Ici on gère seulement ceux à variantes
     multiples : on va chercher les variantes à la demande via /products/x.js
     (rien n'est chargé tant que la cliente ne clique pas) et on les liste
     groupées par technique, comme sur la fiche produit. */
  const initQuickAdd = () => {
    const modal = document.getElementById('ee-quick-add');
    if (!modal) return;

    const titleEl = document.getElementById('ee-quick-add-title');
    const bodyEl = document.getElementById('ee-quick-add-body');
    const cache = {};

    const close = () => {
      modal.classList.remove('ee-active');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    const open = () => {
      modal.classList.add('ee-active');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    const render = (product) => {
      titleEl.textContent = product.title;

      const available = product.variants.filter((v) => v.available);
      if (!available.length) {
        bodyEl.innerHTML = '<p class="ee-quick-add__empty">' + escapeHtml(window.EE.strings.quickAddSoldOut) + '</p>';
        return;
      }

      // Groupe par option1 (la technique). Sur les produits sans ce schéma,
      // option1 sert quand même de regroupement neutre.
      const groups = [];
      const byName = {};
      available.forEach((v) => {
        const key = v.option1 || '';
        if (!byName[key]) {
          byName[key] = [];
          groups.push(key);
        }
        byName[key].push(v);
      });

      let html = '';
      groups.forEach((g) => {
        if (g && groups.length > 1) {
          html += '<div class="ee-quick-add__group">' + escapeHtml(g) + '</div>';
        }
        byName[g].forEach((v) => {
          const label = v.option2 || v.title;
          html += '<button class="ee-quick-add__variant" type="button" data-variant-id="' + escapeHtml(v.id) + '">' +
                    '<span class="ee-quick-add__variant-name">' + escapeHtml(label) + '</span>' +
                    '<span class="ee-quick-add__variant-price">' + formatMoney(v.price) + '</span>' +
                  '</button>';
        });
      });
      bodyEl.innerHTML = html;
    };

    // Ouverture depuis une carte
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-quick-add]');
      if (!trigger) return;
      e.preventDefault();

      const handle = trigger.getAttribute('data-quick-add');
      titleEl.textContent = '';
      bodyEl.innerHTML = '<div class="ee-quick-add__loading"><span class="ee-spinner"></span></div>';
      open();

      if (cache[handle]) {
        render(cache[handle]);
        return;
      }

      fetch(window.EE.routes.root_url.replace(/\/$/, '') + '/products/' + handle + '.js')
        .then((r) => {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then((product) => {
          cache[handle] = product;
          render(product);
        })
        .catch(() => {
          bodyEl.innerHTML = '<p class="ee-quick-add__empty">' + escapeHtml(window.EE.strings.quickAddLoadError) + ' ' +
                             '<a href="' + escapeHtml(window.EE.routes.root_url.replace(/\/$/, '') + '/products/' + handle) + '">' +
                             escapeHtml(window.EE.strings.quickAddViewProduct) + '</a></p>';
        });
    });

    // Choix d'une variante → ajout au panier
    bodyEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-variant-id]');
      if (!btn) return;

      const id = btn.getAttribute('data-variant-id');
      btn.disabled = true;
      btn.classList.add('ee-loading');

      fetch(window.EE.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: id, quantity: 1 })
      })
        .then((r) => r.json())
        .then((item) => {
          if (item.status) throw new Error(item.description || item.message);
          close();
          updateCartDrawer();
          const drawer = document.getElementById('ee-cart-drawer');
          if (drawer) {
            drawer.classList.add('ee-active');
            document.body.style.overflow = 'hidden';
          }
          showNotification(item.title + ' ' + window.EE.strings.addedToCartSuffix, 'success');
        })
        .catch(() => {
          showNotification(window.EE.strings.genericError, 'error');
        })
        .finally(() => {
          btn.disabled = false;
          btn.classList.remove('ee-loading');
        });
    });

    modal.querySelectorAll('[data-quick-close]').forEach((el) => {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('ee-active')) close();
    });
  };

  /* ---- Change Cart Item ---- */
  const changeCartItem = (key, qty) => {
    fetch(window.EE.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
    .then(r => r.json())
    .then(() => updateCartDrawer());
  };

  /* ---- Product Variant Selection ---- */
  const initVariantSelector = () => {
    document.querySelectorAll('.ee-variant-select').forEach(select => {
      select.addEventListener('change', (e) => {
        const form = e.target.closest('form');
        if (!form) return;
        const variantId = e.target.value;
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) idInput.value = variantId;

        // Update price display
        const priceEl = form.closest('.ee-product-detail__info, .ee-product-detail')
          ?.querySelector('.ee-product-detail__price');
        const selectedOption = e.target.options[e.target.selectedIndex];
        if (priceEl && selectedOption.dataset.price) {
          priceEl.innerHTML = formatMoney(parseInt(selectedOption.dataset.price));
        }
      });
    });
  };

  /* ---- Variant Boxes (rangées de boîtes toujours visibles) ----
     Remplace l'ancien menu déroulant fermé. Deux modes :
     - technique + longueur : cliquer une technique filtre les boîtes de
       longueur (data-tech) pour n'afficher que celles de cette technique,
       puis sélectionne la première disponible.
     - simple : une seule rangée de boîtes (produits à une seule option). */
  const initVariantBoxes = () => {
    const wrap = document.getElementById('ee-pd-variants');
    if (!wrap) return;
    const hiddenSelect = wrap.querySelector('#ee-variant-select');

    const selectVariant = (box) => {
      if (!box || !box.dataset.value) return;
      wrap.querySelectorAll('.ee-opt-box[data-value]').forEach((b) => b.classList.remove('is-active'));
      box.classList.add('is-active');
      if (hiddenSelect) {
        hiddenSelect.value = box.dataset.value;
        hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    const techBoxes = wrap.querySelectorAll('#ee-pd-tech-boxes .ee-opt-box');
    const lengthBoxes = wrap.querySelectorAll('#ee-pd-length-boxes .ee-opt-box');

    techBoxes.forEach((tBox) => {
      tBox.addEventListener('click', () => {
        techBoxes.forEach((b) => b.classList.remove('is-active'));
        tBox.classList.add('is-active');

        let firstMatch = null;
        lengthBoxes.forEach((lBox) => {
          const matches = lBox.dataset.tech === tBox.dataset.tech;
          lBox.style.display = matches ? '' : 'none';
          if (matches && !firstMatch) firstMatch = lBox;
        });
        selectVariant(firstMatch);
      });
    });

    lengthBoxes.forEach((lBox) => {
      lBox.addEventListener('click', () => selectVariant(lBox));
    });

    document.querySelectorAll('#ee-pd-simple-boxes .ee-opt-box').forEach((box) => {
      box.addEventListener('click', () => selectVariant(box));
    });
  };

  /* ---- Generic Custom Select (dropdowns, never the native OS one) ---- */
  const initCustomSelect = () => {
    document.querySelectorAll('.ee-custom-select').forEach((wrap) => {
      const current = wrap.querySelector('.ee-custom-select__current');
      const opts = wrap.querySelectorAll('.ee-custom-select__opt');
      const hiddenSelect = document.getElementById(wrap.dataset.selectTarget);

      opts.forEach((opt) => {
        opt.addEventListener('click', () => {
          opts.forEach((o) => o.classList.remove('is-active'));
          opt.classList.add('is-active');
          current.textContent = opt.textContent;
          current.classList.toggle('ee-custom-select__placeholder', opt.dataset.placeholder === 'true');
          wrap.classList.remove('is-open');

          if (hiddenSelect) {
            hiddenSelect.value = opt.dataset.value;
            hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    });

    document.addEventListener('click', (e) => {
      document.querySelectorAll('.ee-custom-select.is-open').forEach((el) => {
        if (!el.contains(e.target)) el.classList.remove('is-open');
      });
    });
  };

  /* ---- Image Lightbox ---- */
  const initLightbox = () => {
    const lightbox = document.getElementById('ee-lightbox');
    if (!lightbox) return;
    const lightboxImg = document.getElementById('ee-lightbox-img');
    const zoomBtn = document.getElementById('ee-zoom-btn');
    const closeBtn = document.getElementById('ee-lightbox-close');

    const openLightbox = () => {
      const mainImage = document.getElementById('ee-main-product-image');
      if (!mainImage) return;
      lightboxImg.src = mainImage.src;
      lightboxImg.alt = mainImage.alt || '';
      lightbox.classList.add('is-open');
    };
    const closeLightbox = () => lightbox.classList.remove('is-open');

    if (zoomBtn) zoomBtn.addEventListener('click', openLightbox);
    if (closeBtn) closeBtn.addEventListener('click', closeLightbox);
    lightbox.addEventListener('click', (e) => { if (e.target === lightbox) closeLightbox(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeLightbox(); });
  };

  /* ---- Notify Me (back in stock) ---- */
  const initNotifyForm = () => {
    const wrap = document.getElementById('ee-notify-form');
    if (!wrap) return;
    if (window.location.search.indexOf('contact_posted=true') !== -1) {
      wrap.classList.add('is-open', 'is-success');
    }
  };

  /* ---- Product Image Gallery ---- */
  const initGallery = () => {
    document.querySelectorAll('.ee-product-detail__thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        const mainImage = thumb.closest('.ee-product-detail__gallery')
          ?.querySelector('.ee-product-detail__main-image img');
        if (!mainImage) return;

        const newSrc = thumb.dataset.full || thumb.querySelector('img').src;
        mainImage.src = newSrc;

        // Update active state
        thumb.parentElement.querySelectorAll('.ee-product-detail__thumb').forEach(t => t.classList.remove('ee-active'));
        thumb.classList.add('ee-active');
      });
    });
  };

  /* ---- Notification ---- */
  const showNotification = (message, type) => {
    let notification = document.querySelector('.ee-notification');
    if (!notification) {
      notification = document.createElement('div');
      notification.className = 'ee-notification';
      document.body.appendChild(notification);
    }
    notification.className = 'ee-notification ee-notification--' + type;
    notification.textContent = message;
    setTimeout(() => notification.classList.add('ee-active'), 10);
    setTimeout(() => notification.classList.remove('ee-active'), 3500);
  };

  /* ---- Helpers ---- */
  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return '$' + amount + ' CAD';
  };

  const getSizedImageUrl = (url, size) => {
    if (!url) return '';
    return url.replace(/(\.[^.]+)$/, '_' + size + '$1');
  };

  /* Échappe aussi les guillemets : indispensable puisque le résultat est inséré
     DANS des attributs HTML. textContent seul ne couvre que < > & — une valeur
     contenant " permettrait de sortir de l'attribut et d'injecter du code. */
  const escapeHtml = (str) => {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  /* ---- Quantity Buttons ---- */
  const initQuantity = () => {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.ee-quantity__btn');
      if (!btn) return;
      const input = btn.parentElement.querySelector('.ee-quantity__input');
      if (!input) return;
      let val = parseInt(input.value) || 1;
      if (btn.dataset.action === 'decrease') val = Math.max(1, val - 1);
      if (btn.dataset.action === 'increase') val = val + 1;
      input.value = val;
      input.dispatchEvent(new Event('change'));
    });
  };

  /* ---- Perf: met en pause le reflet or hors écran ----
     background-position n'est pas composité par le GPU : chaque frame repeint.
     Avec ~60 éléments animés, ça sature le CPU. On ne laisse tourner que le visible. */
  const initShimmerPerf = () => {
    if (!('IntersectionObserver' in window)) return;
    const sections = document.querySelectorAll('.shopify-section, .ee-footer, .ee-header');
    if (!sections.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        e.target.classList.toggle('ee-offscreen', !e.isIntersecting);
      });
    }, { rootMargin: '200px 0px' }); // reprend juste avant d'entrer dans le champ

    sections.forEach((s) => io.observe(s));
  };

  /* ---- Init Everything ----
     Chaque module est isolé : si l'un plante, les autres continuent de fonctionner
     (avant, une seule erreur cassait tout ce qui suivait dans la liste). */
  const boot = () => {
    window.__eeBooted = true;
    [
      initScrollAnimations, initHeroVideoLoop, initHeaderScroll, initMobileMenu, initAccordions,
      initCartDrawer, initAddToCart, initQuickAdd, initVariantSelector, initVariantBoxes,
      initCustomSelect, initGallery, initLightbox, initNotifyForm,
      initShimmerPerf, initQuantity
    ].forEach((fn) => {
      try { fn(); } catch (err) { console.error('[EE] ' + fn.name + ' :', err); }
    });
  };

  // Si le script se charge après DOMContentLoaded, l'écouteur ne se déclencherait jamais.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
